import { msg } from "@lingui/core/macro";
import type { SortingState } from "@tanstack/react-table";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { IPatientsTableItem } from "./PatientsTable";
import { PatientsTable } from "./PatientsTable";
import type { IListExistingPatient } from "@/api/Patients";
import {
  informationFieldConfigValueSchema,
  usePatientInformationFields,
  usePatientTypes,
  useTeams,
} from "@/api/Patients";
import { usePrimaryResponsibleEmployees } from "@/api/Employee";
import { employeeName, type IEmployee } from "@medoma/models/shifts";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import NoResults from "@/components/NoResults/NoResults";
import { useMemo, useState } from "react";
import { NotificationsCell } from "./NotificationsCell";
import { FamilyNameCell, GivenNameCell } from "./NameCell";
import LoggedInStatusCell from "./LoggedInStatusCell";
import { PostDischargeProcessedCell } from "./PostDischargeProcessedCell";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import * as Sentry from "@sentry/react";
import { getPatientTableColumnDisplayNames } from "./patientsTableUtils";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import {
  formatPersonalIdentityNumber,
  getPatientFamilyName,
  getPatientGivenName,
  patientTypeDictionary,
} from "@medoma/models/patients";
import { Text } from "@medoma/components/Text/Text";
import {
  organizationSchema,
  type IOrganization,
} from "@medoma/models/organizations";
import { useUserProfile } from "@/api/Users";
import { dateMonthAndYear } from "@/Utils/DateUtils";

type IPatientsItem = IPatientsTableItem<IListExistingPatient> & {
  admittedAt?: Date | null | undefined;
  pausedAt?: Date | null | undefined;
  dischargedAt?: Date | null | undefined;
  plannedAdmissionDateTime?: Date | null | undefined;
  plannedDischargeDateTime?: Date | null | undefined;
  postDischargeProcessed?: boolean | null | undefined;
  isSignedInToCare?: boolean;
  primaryResponsible: number | null | undefined;
  placeOfAssessment: string | null | undefined;
  referringUnit: string | null | undefined;
};

const columnHelper = createColumnHelper<IPatientsItem>();

type AdditionalColumn = keyof Pick<
  IPatientsItem,
  | "admittedAt"
  | "pausedAt"
  | "dischargedAt"
  | "plannedAdmissionDateTime"
  | "plannedDischargeDateTime"
>;

type IPatientWithSignIn = IListExistingPatient & { isSignedInToCare: boolean };

interface PatientsTableContainerBaseProps {
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  additionalColumns?: AdditionalColumn[];
  showNotifications: boolean;
  showPatientStatus: boolean;
  showPrimaryResponsible: boolean;
  showPlaceOfAssessment: boolean;
  showPostDischargeProcessed: boolean;
  loadingMessage: MessageDescriptor;
  errorMessage: MessageDescriptor;
  noResultsMessage: MessageDescriptor;
}

type PatientsTableContainerProps =
  | (PatientsTableContainerBaseProps & {
      showPatientSignInStatus: true;
      patients: IPatientWithSignIn[] | undefined;
    })
  | (PatientsTableContainerBaseProps & {
      showPatientSignInStatus: false;
      patients: IListExistingPatient[] | undefined;
    });

const patientListShowsReferringUnit = (
  organization: IOrganization,
): boolean => {
  switch (organization) {
    case organizationSchema.enum.hahag:
      return true;
    case organizationSchema.enum.capioahc:
    case organizationSchema.enum.ksgr:
    case organizationSchema.enum.medoma:
    case organizationSchema.enum.qa:
    case organizationSchema.enum.rakus:
    case organizationSchema.enum.slso:
    case organizationSchema.enum.ssh:
    case organizationSchema.enum.sus:
      return false;
    default:
      throw organization satisfies never;
  }
};

const getDataFromPatients = (
  patients: (IListExistingPatient & { isSignedInToCare?: boolean })[],
) => {
  return patients.map((patient) => {
    const {
      id,
      ward,
      personalIdentityNumber,
      personalIdentityNumberType,
      patientType,
      area,
      team,
      primaryResponsible,
      placeOfAssessment,
      referringUnit,
      isSignedInToCare,
    } = patient;
    return {
      patient,
      familyName: getPatientFamilyName(patient.name),
      givenName: getPatientGivenName(patient.name),
      notifications: id,
      ward,
      personalIdentityNumber,
      personalIdentityNumberType,
      referringUnit,
      patientType,
      area,
      team,
      primaryResponsible,
      placeOfAssessment,
      admittedAt: "admittedAt" in patient ? patient.admittedAt : undefined,
      pausedAt: "pausedAt" in patient ? patient.pausedAt : undefined,
      dischargedAt:
        "dischargedAt" in patient ? patient.dischargedAt : undefined,
      plannedAdmissionDateTime:
        "plannedAdmissionDateTime" in patient
          ? patient.plannedAdmissionDateTime
          : undefined,
      plannedDischargeDateTime:
        "plannedDischargeDateTime" in patient
          ? patient.plannedDischargeDateTime
          : undefined,
      postDischargeProcessed: patient.postDischargeProcessed,
      isSignedInToCare,
    };
  });
};

const createColumns = (
  translator: ReturnType<typeof useLingui>["t"],
  organization: IOrganization,
  additionalColumns: AdditionalColumn[] = [],
  showNotifications: boolean,
  showPatientStatus: boolean,
  showPatientSignInStatus: boolean,
  showPostDischargeProcessed: boolean,
  employeesMap: Map<number, IEmployee>,
) => {
  const columnDisplayNames = {
    ...getPatientTableColumnDisplayNames(organization),
    admittedAt: msg`Inskriven`,
    pausedAt: msg`Pausad`,
    dischargedAt: msg`Utskriven`,
    plannedAdmissionDateTime: msg`Planerad inskrivning`,
    plannedDischargeDateTime: msg`Planerad utskrivning`,
    postDischargeProcessed: msg`Efterhanterad`,
  };

  const baseColumns = [
    columnHelper.accessor("familyName", {
      id: "familyName",
      header: translator(columnDisplayNames["familyName"]),
      cell: ({ row: { original } }) => {
        const patient = original.patient;
        return (
          <FamilyNameCell
            patient={patient}
            showPatientStatus={showPatientStatus}
          />
        );
      },
      sortingFn: "text",
    }),
    columnHelper.accessor("givenName", {
      id: "givenName",
      header: translator(columnDisplayNames["givenName"]),
      cell: ({ row: { original } }) => {
        const patient = original.patient;
        return <GivenNameCell patient={patient} />;
      },
      sortingFn: "text",
    }),
    columnHelper.accessor("personalIdentityNumber", {
      id: "personalIdentityNumber",
      header: translator(columnDisplayNames["personalIdentityNumber"]),
      cell: ({ row }) => {
        const { personalIdentityNumber, personalIdentityNumberType } =
          row.original.patient;
        return (
          <Text element="span">
            {formatPersonalIdentityNumber({
              personalIdentityNumber,
              personalIdentityNumberType,
            })}
          </Text>
        );
      },
      sortingFn: "text",
    }),
    columnHelper.accessor("referringUnit", {
      id: "referringUnit",
      header: translator(columnDisplayNames["referringUnit"]),
      cell: ({ getValue }) => getValue() ?? "",
      sortingFn: "text",
    }),
    columnHelper.accessor("patientType", {
      id: "patientType",
      header: translator(columnDisplayNames["patientType"]),
      cell: ({ getValue }) => {
        const patientType = getValue();
        return translator(patientTypeDictionary[patientType]);
      },
      sortingFn: "text",
    }),
    columnHelper.accessor("area", {
      id: "area",
      header: translator(columnDisplayNames["area"]),
      cell: ({ getValue }) => {
        const area = getValue();
        return area ? area.displayName : "";
      },
      sortingFn: (rowA, rowB, columnId) => {
        const areaA = rowA.getValue<typeof rowA.original.area>(columnId);
        const areaB = rowB.getValue<typeof rowB.original.area>(columnId);
        const nameA = areaA?.displayName ?? "";
        const nameB = areaB?.displayName ?? "";
        return nameA.localeCompare(nameB);
      },
    }),
    columnHelper.accessor("team", {
      id: "team",
      header: translator(columnDisplayNames["team"]),
      cell: ({ getValue }) => {
        const team = getValue();
        return team ? team.displayName : "";
      },
      sortingFn: (rowA, rowB, columnId) => {
        const teamA = rowA.getValue<typeof rowA.original.team>(columnId);
        const teamB = rowB.getValue<typeof rowB.original.team>(columnId);
        const nameA = teamA?.displayName ?? "";
        const nameB = teamB?.displayName ?? "";
        return nameA.localeCompare(nameB);
      },
    }),
    columnHelper.accessor("primaryResponsible", {
      id: "primaryResponsible",
      header: translator(columnDisplayNames["primaryResponsible"]),
      cell: ({ getValue }) => {
        const primaryResponsible = getValue();
        if (!primaryResponsible) {
          return "";
        }
        const employee = employeesMap.get(primaryResponsible);
        return employee
          ? employeeName(employee, { employeeNameFormat: "full" })
          : "";
      },
      sortingFn: (rowA, rowB, columnId) => {
        const idA = rowA.getValue<number | null | undefined>(columnId);
        const idB = rowB.getValue<number | null | undefined>(columnId);

        // Sort patients without primary responsible to the top
        if (!idA) {
          return -1;
        }
        if (!idB) {
          return 1;
        }

        const employeeA = employeesMap.get(idA);
        const employeeB = employeesMap.get(idB);

        if (!employeeA) {
          return -1;
        }
        if (!employeeB) {
          return 1;
        }

        const nameA = employeeName(employeeA, { employeeNameFormat: "full" });
        const nameB = employeeName(employeeB, { employeeNameFormat: "full" });
        return nameA.localeCompare(nameB);
      },
    }),
    columnHelper.accessor("placeOfAssessment", {
      id: "placeOfAssessment",
      header: translator(columnDisplayNames["placeOfAssessment"]),
      cell: ({ getValue }) => getValue() ?? "",
      sortingFn: "text",
    }),
    columnHelper.accessor("ward", {
      id: "ward",
      header: translator(columnDisplayNames["ward"]),
      cell: ({ getValue }) => {
        const ward = getValue();
        return ward.displayName;
      },
      sortingFn: (rowA, rowB, columnId) => {
        const wardA = rowA.getValue<typeof rowA.original.ward>(columnId);
        const wardB = rowB.getValue<typeof rowB.original.ward>(columnId);
        return wardA.displayName.localeCompare(wardB.displayName);
      },
    }),
  ];

  const extraColumns = [];

  if (additionalColumns.includes("admittedAt")) {
    extraColumns.push(
      columnHelper.accessor("admittedAt", {
        id: "admittedAt",
        header: translator(columnDisplayNames["admittedAt"]),
        cell: ({ getValue }) => {
          const admittedAtDate = getValue();
          return admittedAtDate ? dateMonthAndYear(admittedAtDate) : null;
        },
        sortingFn: "datetime",
      }),
    );
  }

  if (additionalColumns.includes("pausedAt")) {
    extraColumns.push(
      columnHelper.accessor("pausedAt", {
        id: "pausedAt",
        header: translator(columnDisplayNames["pausedAt"]),
        cell: ({ getValue }) => {
          const pausedAtDate = getValue();
          return pausedAtDate ? dateMonthAndYear(pausedAtDate) : null;
        },
        sortingFn: "datetime",
      }),
    );
  }

  if (additionalColumns.includes("plannedAdmissionDateTime")) {
    extraColumns.push(
      columnHelper.accessor("plannedAdmissionDateTime", {
        id: "plannedAdmissionDateTime",
        header: translator(columnDisplayNames["plannedAdmissionDateTime"]),
        cell: ({ getValue }) => {
          const date = getValue();
          return date ? dateMonthAndYear(date) : null;
        },
        sortingFn: "datetime",
      }),
    );
  }

  if (additionalColumns.includes("plannedDischargeDateTime")) {
    extraColumns.push(
      columnHelper.accessor("plannedDischargeDateTime", {
        id: "plannedDischargeDateTime",
        header: translator(columnDisplayNames["plannedDischargeDateTime"]),
        cell: ({ getValue }) => {
          const date = getValue();
          return date ? dateMonthAndYear(date) : null;
        },
        sortingFn: "datetime",
      }),
    );
  }

  if (additionalColumns.includes("dischargedAt")) {
    extraColumns.push(
      columnHelper.accessor("dischargedAt", {
        id: "dischargedAt",
        header: translator(columnDisplayNames["dischargedAt"]),
        cell: ({ getValue }) => {
          const dischargedAtDate = getValue();
          return dischargedAtDate ? dateMonthAndYear(dischargedAtDate) : null;
        },
        sortingFn: "datetime",
      }),
    );
  }

  if (showPatientSignInStatus) {
    extraColumns.push(
      columnHelper.accessor("isSignedInToCare", {
        id: "isSignedInToCare",
        header: translator(msg`Patientapp`),
        cell: ({ getValue }) => (
          <LoggedInStatusCell isSignedIn={getValue() ?? false} /> // treat unknown as signed out
        ),
      }),
    );
  }

  const notificationsColumn = showNotifications
    ? [
        columnHelper.display({
          id: "notifications",
          cell: ({ row: { original } }) => {
            const patientId = original.patient.id;
            return <NotificationsCell patientId={patientId} />;
          },
        }),
      ]
    : [];

  const postDischargeProcessedColumn = showPostDischargeProcessed
    ? [
        columnHelper.accessor("postDischargeProcessed", {
          id: "postDischargeProcessed",
          header: translator(columnDisplayNames["postDischargeProcessed"]),
          cell: ({ row: { original } }) => (
            <PostDischargeProcessedCell
              patientId={original.patient.id}
              postDischargeProcessed={original.patient.postDischargeProcessed}
            />
          ),
          sortingFn: (rowA, rowB, columnId) => {
            const aProcessed =
              rowA.getValue<boolean | null | undefined>(columnId) === true;
            const bProcessed =
              rowB.getValue<boolean | null | undefined>(columnId) === true;
            // Sort unprocessed patients to the top
            if (aProcessed === bProcessed) {
              return 0;
            }
            return aProcessed ? 1 : -1;
          },
        }),
      ]
    : [];

  return [
    ...notificationsColumn,
    ...baseColumns,
    ...extraColumns,
    ...postDischargeProcessedColumn,
  ];
};

export const PatientsTableContainer = ({
  patients,
  isPending,
  isError,
  error,
  additionalColumns = [],
  showNotifications,
  showPatientStatus,
  showPatientSignInStatus,
  showPrimaryResponsible,
  showPlaceOfAssessment,
  showPostDischargeProcessed,
  loadingMessage,
  errorMessage,
  noResultsMessage,
}: PatientsTableContainerProps) => {
  const { t } = useLingui();
  const { organization } = useUserProfile();
  const { data: cardiologyEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.CardiologyWorkflow,
  );
  const { data: areaFeatureFlag } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.Area,
  );

  const { data: patientTypes } = usePatientTypes();
  const { data: teams } = useTeams();
  const {
    employeesMap: primaryResponsibleEmployeesMap,
    isPrimaryResponsibleHidden,
  } = usePrimaryResponsibleEmployees();
  const { data: patientInformationFields } = usePatientInformationFields();
  const isPlaceOfAssessmentHidden =
    patientInformationFields?.PlaceOfAssessment ===
    informationFieldConfigValueSchema.enum.hidden;
  const showReferringUnit =
    patientListShowsReferringUnit(organization) &&
    patientInformationFields?.ReferringUnit !==
      informationFieldConfigValueSchema.enum.hidden;

  const data = useMemo(() => getDataFromPatients(patients || []), [patients]);

  const featuredColumns = useMemo(() => {
    const translatedColumns = createColumns(
      t,
      organization,
      additionalColumns,
      showNotifications,
      showPatientStatus,
      showPatientSignInStatus,
      showPostDischargeProcessed,
      primaryResponsibleEmployeesMap,
    );
    return translatedColumns.filter((column) => {
      return column.id === "ward" && !cardiologyEnabled
        ? false // Don't display ward column if cardiology is not enabled
        : column.id === "patientType" &&
            (!patientTypes || patientTypes.length <= 1)
          ? false // Don't display patient type column if patient type is not fetched or 0-1
          : column.id === "area" && !areaFeatureFlag
            ? false // Don't display area column if area is not enabled
            : column.id === "team" && (!teams || teams.length === 0)
              ? false // Don't display team column if teams is not fetched or empty
              : column.id === "primaryResponsible" &&
                  (isPrimaryResponsibleHidden || !showPrimaryResponsible)
                ? false // Don't display primary responsible column if hidden in config or by prop
                : column.id === "placeOfAssessment" &&
                    (isPlaceOfAssessmentHidden || !showPlaceOfAssessment)
                  ? false // Don't display place of assessment column if hidden in config or by prop
                  : column.id === "referringUnit" && !showReferringUnit
                    ? false // Don't display referring unit column unless the organization uses it and it isn't hidden in config
                    : true;
    });
  }, [
    cardiologyEnabled,
    patientTypes,
    teams,
    areaFeatureFlag,
    t,
    organization,
    additionalColumns,
    showNotifications,
    showPatientStatus,
    showPatientSignInStatus,
    showPostDischargeProcessed,
    primaryResponsibleEmployeesMap,
    isPrimaryResponsibleHidden,
    showPrimaryResponsible,
    isPlaceOfAssessmentHidden,
    showPlaceOfAssessment,
    showReferringUnit,
  ]);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "familyName", desc: false },
  ]);

  // Acceptable. We don't use react compiler, we just lint to find incompatibilities, such as this.
  // oxlint-disable-next-line react/incompatible-library
  const table = useReactTable({
    data,
    columns: featuredColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  if (isPending) {
    return <Loading message={t(loadingMessage)} padding={24} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t(errorMessage)}. ${deducedError(error)}`}
        padding={24}
      />
    );
  }

  if (!patients || patients.length === 0) {
    return <NoResults message={t(noResultsMessage)} padding={24} />;
  }

  return <PatientsTable<IPatientsItem> table={table} />;
};
